import React, { useRef } from "react";

import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { Images } from "@/assets/images";
import { useSerialPorts } from "@/hooks/useSerialPorts";
import { useStyles } from "@/hooks/useStyles.ts";
import { TandemVersionInfo } from "@/lib/drivers/tandem/commands/Version";
import { TandemConstants } from "@/lib/drivers/tandem/TandemConstants";
import { TandemDriver } from "@/lib/drivers/tandem/TandemDriver";
import { TandemData } from "@/lib/drivers/tandem/TandemTypes";
import { MedicalDevice } from "@/models/DeviceModel";
import { Queries } from "@/queries/Queries";
import { Theme } from "@/theme/CreateTheme.ts";
import { BaseDeviceCardProps, DeviceCard } from "@/uiKit/atoms/device-card/DeviceCard";
import { DeviceSourcesStatus } from "@/uiKit/atoms/device-card/DeviceSourcesStatus";
import { DeviceUploadAction } from "@/uiKit/atoms/device-card/DeviceUploadAction";
import { LastDeviceUpload } from "@/uiKit/atoms/device-card/LastDeviceUpload";
import { TandemSearchButton } from "@/uiKit/molecules/TandemSearchButton.tsx";
import { ProgressListener } from "@/utils/ProgressMixin";
import { Sentry } from "@/utils/sentryUtils";

export type TandemDeviceType = [SerialPort, TandemVersionInfo, TandemData];

export const TANDEM_DEVICE_CARD_ID = "tandem";

const tandemDriver = async (
  devices: MedicalDevice[],
  progressId: string,
  progressListener: ProgressListener,
  source: SerialPort,
) => {
  console.info("Setting up Tandem driver");
  try {
    const driver = new TandemDriver(source, devices);
    driver.subscribeToProgress(progressId, progressListener);
    return driver;
  } catch (e) {
    Sentry.captureException(e);
    console.error("Error", e);
  }
  return undefined;
};

export const TandemDeviceCard: React.FC<BaseDeviceCardProps> = ({
  onRawData,
  onData,
  onError,
  progress,
  progressListener,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  const openDevices = Queries.device.useDevicesByType(TandemConstants.API_DEVICE_NAME_OPEN);
  const closedDevices = Queries.device.useDevicesByType(TandemConstants.API_DEVICE_NAME_CLOSED);

  const sources = useSerialPorts([
    {
      vendorId: TandemConstants.VENDOR_ID,
      productId: TandemConstants.PRODUCT_ID,
    },
  ]);

  const driverRef = useRef<TandemDriver | undefined>(undefined);

  const onUpload = async () => {
    const driver = await tandemDriver(
      [...openDevices, ...closedDevices],
      TANDEM_DEVICE_CARD_ID,
      progressListener,
      sources[0],
    );
    driverRef.current = driver;
    console.info("Driver initialized", driver ? "successfully" : "unsuccessfully");
    if (driver) {
      const onErrorWrapped = (message: string) => onError(t(message));
      await driver.fetchAndUpload(onData, onErrorWrapped, onRawData);
    } else {
      onError(t("devices.errors.driverInit"));
    }
  };

  const onCancel = () => {
    driverRef.current?.disconnect();
    onError(t("devices.errors.cancelled"));
  };

  return (
    <DeviceCard
      deviceName={t("devices.tandem.name")}
      deviceInstructions={
        sources.length > 0 ? t("devices.tandem.instructions") : t("devices.tandem.instructionsNoDevices")
      }
      action={
        <div className={styles.action}>
          {sources.length > 0 ? (
            <DeviceUploadAction
              onUpload={onUpload}
              onCancel={onCancel}
              progress={progress}
              canUpload={sources.length === 1 && !progress}
            />
          ) : (
            <TandemSearchButton />
          )}
        </div>
      }
    >
      <Images.tandemPump alt={t("devices.tandem.name")} width={100} />
      <div>
        <LastDeviceUpload devices={[...openDevices, ...closedDevices]} />
        <DeviceSourcesStatus
          sources={sources}
          noDeviceText={t("devices.tandem.noDeviceFound")}
          deviceDetectedText={t("devices.tandem.deviceDetected")}
          manyDevicesDetectedText={t("devices.tandem.manyDevicesDetected")}
        />
      </div>
    </DeviceCard>
  );
};

const makeStyles = (theme: Theme) => ({
  action: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: theme.spacing(4),
  }),
});
